import { z } from 'zod';
import { ALLOCATION_TYPES, BILLABLE_FLAGS, BUSINESS_UNIT_KINDS, COUNTRY_CODES, CURRENCIES, EMPLOYEE_STATUSES, EMPLOYMENT_TYPES, LEDGER_PARTY_ROLES, LEDGER_TYPES, VENDOR_ADVANCE_MODES, WORK_MODES, } from '../enums.js';
import { PERMISSIONS } from '../permissions.js';
import { bankAccountNumber, basisPoints, gstin, iban, ifsc, isoDate, minorAmount, objectId, paginationQuery, pan, routingCode, scopeQuery, swiftBic, tdsSectionCode, } from './common.js';
export const currency = z.enum(CURRENCIES);
export const countryCode = z.enum(COUNTRY_CODES);
/**
 * A role key: the eight built into the product plus whatever a tenant defines.
 *
 * Deliberately a pattern rather than an enum of `ROLE_KEYS` — custom roles are
 * rows, so the closed list moved to a database lookup in the route, which is
 * the only place that knows the tenant.
 */
export const roleKey = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9_]{1,39}$/, 'Role keys are uppercase letters, digits and underscores');
export const permission = z.enum(PERMISSIONS);
export const createCompanyRequest = z.object({
    name: z.string().trim().min(2).max(160),
    groupId: objectId.optional().or(z.literal('')),
    legalName: z.string().trim().max(200).optional(),
    gstin: gstin.optional().or(z.literal('')),
    cin: z.string().trim().max(40).optional(),
    invoiceInboxAddress: z.string().trim().email().optional().or(z.literal('')),
    /** Where the company is registered. India-only rules apply only to `IN`. */
    country: countryCode.default('IN'),
    /**
     * The one currency this company books and pays in. Defaults to the
     * country's currency; an Indian company always books in INR. Locked once
     * the company has invoices.
     */
    baseCurrency: currency.optional(),
});
export const updateCompanyRequest = createCompanyRequest.partial().extend({
    active: z.boolean().optional(),
});
/** System Settings: only the switches/settings sent change. */
export const updateCompanySettingsRequest = z
    .object({
    tdsEnabled: z.boolean(),
    ledgerRequired: z.boolean(),
    dueDateReminderDays: z.number().int().min(0).max(90),
    loanReleaseDays: z.number().int().min(1).max(60),
})
    .partial()
    .strict()
    .refine((body) => Object.keys(body).length > 0, 'Send at least one setting');
/**
 * A group sits above the legal entity, so unlike every other org level it
 * carries no `companyId`.
 */
export const createGroupRequest = z.object({
    name: z.string().trim().min(2).max(160),
    code: z.string().trim().min(1).max(20).toUpperCase(),
});
export const updateGroupRequest = createGroupRequest
    .partial()
    .extend({ active: z.boolean().optional() });
export const createRegionRequest = z.object({
    companyId: objectId,
    name: z.string().trim().min(2).max(120),
    code: z.string().trim().min(1).max(20).toUpperCase(),
});
export const updateRegionRequest = createRegionRequest
    .omit({ companyId: true })
    .partial()
    .extend({ active: z.boolean().optional() });
export const createVerticalRequest = z.object({
    companyId: objectId,
    name: z.string().trim().min(2).max(120),
    code: z.string().trim().min(1).max(20).toUpperCase(),
    headUserId: objectId.optional().or(z.literal('')),
});
export const updateVerticalRequest = createVerticalRequest
    .omit({ companyId: true })
    .partial()
    .extend({ active: z.boolean().optional() });
export const createBusinessUnitRequest = z.object({
    companyId: objectId,
    /** A unit can serve more than one vertical, e.g. Facilities for Technology and Operations. */
    verticalIds: z.array(objectId).min(1, 'Choose at least one vertical').max(20),
    name: z.string().trim().min(2).max(120),
    code: z.string().trim().min(1).max(20).toUpperCase(),
    kind: z.enum(BUSINESS_UNIT_KINDS).default('STANDARD'),
});
export const updateBusinessUnitRequest = createBusinessUnitRequest
    .omit({ companyId: true })
    .partial()
    .extend({ active: z.boolean().optional() });
/**
 * One city per location. A comma-joined list ("Chennai, Vellore") is two
 * places in one row — each should be its own location.
 */
const singleCity = z
    .string()
    .trim()
    .max(80)
    .refine((value) => !/[,;/|]/.test(value), 'One city per location — add another location instead');
export const createLocationRequest = z.object({
    companyId: objectId,
    /** Required: every location rolls up into one region of the same company. */
    regionId: objectId,
    name: z.string().trim().min(2).max(120),
    code: z.string().trim().min(1).max(20).toUpperCase(),
    city: singleCity.optional(),
    state: z.string().trim().max(80).optional(),
});
export const updateLocationRequest = createLocationRequest
    .omit({ companyId: true })
    .partial()
    .extend({ active: z.boolean().optional() });
/**
 * A department sits in exactly one business unit, and in one of that unit's
 * verticals. When the unit has a single vertical the server takes it from the
 * unit; when the unit serves several, the department names which one, and the
 * server checks it is one of the unit's.
 */
export const createDepartmentRequest = z.object({
    companyId: objectId,
    businessUnitId: objectId,
    verticalId: objectId.optional().or(z.literal('')),
    name: z.string().trim().min(2).max(120),
    code: z.string().trim().min(1).max(20).toUpperCase(),
    headUserId: objectId.optional().or(z.literal('')),
});
export const updateDepartmentRequest = createDepartmentRequest
    .omit({ companyId: true })
    .partial()
    .extend({ active: z.boolean().optional() });
export const createUserRequest = z.object({
    name: z.string().trim().min(2).max(120),
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(10).optional(),
    /** Exactly one role per user; a combination is modelled as its own role. */
    roleKey,
    // An empty array on any axis means "unrestricted on that axis". Capped
    // because every one of these arrays travels in the access token.
    companyIds: z.array(objectId).max(200).default([]),
    groupIds: z.array(objectId).max(200).default([]),
    regionIds: z.array(objectId).max(200).default([]),
    verticalIds: z.array(objectId).max(200).default([]),
    businessUnitIds: z.array(objectId).max(200).default([]),
    locationIds: z.array(objectId).max(200).default([]),
    departmentIds: z.array(objectId).max(200).default([]),
    /**
     * The vendor rows a Vendor-role login answers for. Required for that role
     * and refused for every other, which the server enforces with the role.
     */
    vendorIds: z.array(objectId).max(200).default([]),
});
export const updateUserRequest = createUserRequest.partial().extend({
    status: z.enum(['ACTIVE', 'INVITED', 'SUSPENDED']).optional(),
});
export const createRoleRequest = z.object({
    label: z.string().trim().min(2).max(60),
    /** Derived from the label when omitted, so the form only asks for a name. */
    key: roleKey.optional(),
    description: z.string().trim().max(240).optional(),
    permissions: z.array(permission).min(1, 'Select at least one permission'),
});
export const updateRoleRequest = createRoleRequest
    .omit({ key: true })
    .partial()
    .extend({ active: z.boolean().optional() });
/**
 * One TDS default a vendor can be paid under. A vendor billing both
 * professional fees and rent needs a different section and rate for each —
 * see `TdsProfile` in `../types.ts`.
 */
export const tdsProfile = z.object({
    label: z.string().trim().min(1).max(60),
    section: tdsSectionCode,
    rateBasisPoints: basisPoints,
    isDefault: z.boolean().default(false),
});
/**
 * A section may be configured more than once — a vendor doing AC and lift
 * maintenance has two 194C profiles, perhaps at different rates — so each
 * profile is told apart by its own name. Exactly one profile (when
 * any exist) is the default. `tdsProfiles` is optional here so a partial
 * update that doesn't touch TDS at all leaves the vendor's profiles alone —
 * only checked when the caller actually sent an array.
 */
function withTdsInvariants(schema) {
    return schema
        .refine((value) => {
        const profiles = value.tdsProfiles ?? [];
        const names = profiles.map((profile) => profile.label.trim().toLowerCase());
        return new Set(names).size === names.length;
    }, { message: 'Give each TDS profile its own name', path: ['tdsProfiles'] })
        .refine((value) => {
        const profiles = value.tdsProfiles ?? [];
        return (profiles.length === 0 || profiles.filter((profile) => profile.isDefault).length === 1);
    }, { message: 'Exactly one TDS profile must be marked default', path: ['tdsProfiles'] });
}
const vendorFields = z.object({
    companyId: objectId,
    code: z.string().trim().min(1).max(30).toUpperCase().optional(),
    name: z.string().trim().min(2).max(200),
    email: z.string().trim().toLowerCase().email().optional().or(z.literal('')),
    phone: z.string().trim().max(30).optional(),
    gstin: gstin.optional().or(z.literal('')),
    pan: pan.optional().or(z.literal('')),
    tdsProfiles: z.array(tdsProfile).max(6).default([]),
    /** Under GST reverse charge by default: its invoices start with RCM ticked. */
    rcm: z.boolean().optional(),
    /** A non-Indian vendor's tax registration (VAT, TRN, EIN…). */
    taxId: z.string().trim().max(40).optional(),
    bankAccountNumber: bankAccountNumber.optional().or(z.literal('')),
    ifsc: ifsc.optional().or(z.literal('')),
    // Outside India a vendor is paid by SWIFT or a local clearing code instead
    // of an IFSC; which set is required depends on the company's country and is
    // checked when the payment is prepared, not here.
    iban: iban.optional().or(z.literal('')),
    swiftBic: swiftBic.optional().or(z.literal('')),
    routingCode: routingCode.optional().or(z.literal('')),
    bankName: z.string().trim().max(120).optional(),
    beneficiaryName: z.string().trim().max(200).optional(),
    paymentTermsDays: z.coerce.number().int().min(0).max(365).default(30),
    notes: z.string().trim().max(1000).optional(),
    /** Pre-fills this vendor's invoices; empty clears it. */
    defaultExpenseLedgerId: objectId.optional().or(z.literal('')),
    /**
     * Set after the user has seen the near-duplicate names the server returned
     * and confirmed this is a different vendor. Never overrides a GSTIN or PAN
     * match, which is the same vendor by definition.
     */
    confirmNotDuplicate: z.boolean().optional(),
});
/**
 * An advance already paid to the vendor, outside the platform — recorded, not
 * executed. The amount is in minor units like every other figure.
 */
export const vendorAdvancePayment = z.object({
    amount: z.number().int('Amount must be an integer number of paise').positive('Enter an amount'),
    paidOn: isoDate,
    mode: z.enum(VENDOR_ADVANCE_MODES),
    reference: z.string().trim().min(1, 'Enter the bank or cheque reference').max(80),
    note: z.string().trim().max(500).optional(),
});
export const createVendorRequest = withTdsInvariants(vendorFields.extend({
    /** Posted as the vendor's first DEPOSIT once the vendor exists. */
    openingAdvance: vendorAdvancePayment.optional(),
}));
export const updateVendorRequest = withTdsInvariants(vendorFields
    .omit({ companyId: true, confirmNotDuplicate: true })
    .partial()
    .extend({
    status: z.enum(['ACTIVE', 'INACTIVE', 'BLOCKED']).optional(),
}));
/** A deposit or top-up on an existing vendor's advance balance. */
export const vendorAdvanceEntryRequest = vendorAdvancePayment.extend({
    type: z.enum(['DEPOSIT', 'TOP_UP']),
});
/** Undoing a mistaken entry needs a reason, which the audit trail keeps. */
export const reverseVendorAdvanceRequest = z.object({
    reason: z.string().trim().min(3, 'Say why, in a few words').max(500),
});
export const createBankAccountRequest = z.object({
    companyId: objectId,
    label: z.string().trim().min(2).max(120),
    bankName: z.string().trim().min(2).max(120),
    // Accepts an IBAN too, which is alphanumeric within the same length.
    accountNumber: bankAccountNumber,
    // Required for accounts of Indian companies; the server checks that, since
    // only it knows the company's country. The currency always follows the company.
    ifsc: ifsc.optional().or(z.literal('')),
    swiftBic: swiftBic.optional().or(z.literal('')),
    currentBalance: z.number().int().default(0),
    bankFileFormat: z.enum(['HDFC', 'ICICI', 'GENERIC_CSV', 'GENERIC_XLSX']).default('GENERIC_XLSX'),
});
export const updateBankAccountRequest = createBankAccountRequest
    .omit({ companyId: true })
    .partial()
    .extend({ active: z.boolean().optional() });
export const CARD_TYPES = ['DEBIT', 'CREDIT'];
export const CARD_NETWORKS = ['VISA', 'MASTERCARD', 'RUPAY', 'AMEX', 'DINERS', 'OTHER'];
/**
 * A company card, held for reference only: the last four digits identify it.
 * The full number and the CVV are never accepted, so they can never be stored.
 */
export const createPaymentCardRequest = z.object({
    companyId: objectId,
    label: z.string().trim().min(2).max(120),
    cardType: z.enum(CARD_TYPES),
    network: z.enum(CARD_NETWORKS).default('VISA'),
    bankName: z.string().trim().min(2).max(120),
    last4: z
        .string()
        .trim()
        .regex(/^\d{4}$/, 'Enter only the last 4 digits'),
    holderName: z.string().trim().min(2).max(120),
    expiry: z
        .string()
        .trim()
        .regex(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Use MM/YY, e.g. 08/29'),
    bankAccountId: objectId.optional().or(z.literal('')),
    /** Minor units; credit cards only. */
    creditLimit: z.number().int('Enter a whole amount').min(0).optional(),
    notes: z.string().trim().max(500).optional(),
});
export const updatePaymentCardRequest = createPaymentCardRequest
    .omit({ companyId: true })
    .partial()
    .extend({ active: z.boolean().optional() });
/**
 * A net banking login the company pays through. The login ID is kept so the
 * team knows which access to use; passwords and PINs are never accepted.
 */
export const createNetBankingRequest = z.object({
    companyId: objectId,
    label: z.string().trim().min(2).max(120),
    bankName: z.string().trim().min(2).max(120),
    loginId: z.string().trim().min(2).max(80),
    bankAccountId: objectId.optional().or(z.literal('')),
    portalUrl: z
        .string()
        .trim()
        .url('Enter a full address, e.g. https://…')
        .optional()
        .or(z.literal('')),
    /** Minor units: the most one day's transfers may total. */
    dailyLimit: z.number().int('Enter a whole amount').min(0).optional(),
    notes: z.string().trim().max(500).optional(),
});
export const updateNetBankingRequest = createNetBankingRequest
    .omit({ companyId: true })
    .partial()
    .extend({ active: z.boolean().optional() });
/** A ledger code as accountants write them: letters, digits and a few separators. */
const ledgerCode = z
    .string()
    .trim()
    .toUpperCase()
    .min(1)
    .max(30)
    .regex(/^[A-Z0-9][A-Z0-9._/-]*$/, 'Use letters, digits, ".", "_", "/" or "-"');
/**
 * One account in a company's chart of accounts. A vendor's own account names
 * the vendor and what it is for; its type then follows from that role.
 */
export const createLedgerRequest = z.object({
    companyId: objectId,
    /** Generated from the name when left blank. */
    code: ledgerCode.optional().or(z.literal('')),
    name: z.string().trim().min(2).max(120),
    type: z.enum(LEDGER_TYPES).optional(),
    group: z.string().trim().max(80).optional().or(z.literal('')),
    vendorId: objectId.optional().or(z.literal('')),
    partyRole: z
        .enum(LEDGER_PARTY_ROLES)
        .optional()
        .or(z.literal('')),
    isDefault: z.boolean().optional(),
});
export const updateLedgerRequest = createLedgerRequest
    .omit({ companyId: true })
    .partial()
    .extend({ active: z.boolean().optional() });
export const ledgerListQuery = paginationQuery.merge(scopeQuery).extend({
    q: z.string().trim().max(120).optional(),
    type: z.enum(LEDGER_TYPES).optional(),
    vendorId: objectId.optional(),
    partyRole: z.enum(LEDGER_PARTY_ROLES).optional(),
    /** `true` lists only general accounts, `false` only vendors' own accounts. */
    general: z.enum(['true', 'false']).optional(),
});
/** One rate a TDS section is deducted at, e.g. "Others" at 2% (200 basis points). */
export const tdsRateOption = z.object({
    label: z.string().trim().min(1, 'Name the rate, e.g. Others').max(60),
    rateBasisPoints: basisPoints.refine((rate) => rate > 0, 'Enter a rate above 0%'),
});
/** A section in the tenant's TDS Sections master, with the rates it is deducted at. */
export const createTdsSectionRequest = z.object({
    code: tdsSectionCode,
    name: z.string().trim().min(2).max(120),
    rates: z
        .array(tdsRateOption)
        .min(1, 'Add at least one rate')
        .max(10)
        .refine((rates) => new Set(rates.map((rate) => rate.rateBasisPoints)).size === rates.length, 'Each rate can be listed once'),
});
export const updateTdsSectionRequest = createTdsSectionRequest.partial().extend({
    active: z.boolean().optional(),
});
/**
 * Replaces a section with a new code carrying the same rules, e.g. 194IB with
 * 194IC. The name and rates are the old section's unless given; the old one
 * is kept, read-only, as a previous version.
 */
export const replaceTdsSectionRequest = z.object({
    code: tdsSectionCode,
    name: z.string().trim().min(2).max(120).optional(),
    rates: createTdsSectionRequest.shape.rates.optional(),
    /** When the new code takes over; today when left out. */
    effectiveFrom: isoDate.optional(),
});
export const tdsSectionListQuery = paginationQuery.extend({
    q: z.string().trim().max(60).optional(),
    /** Also list sections replaced by a newer code, as Settings shows them. */
    includeReplaced: z.enum(['true', 'false']).optional(),
});
// ── Employee master (HR) ──────────────────────────────────
const employeeFields = {
    employeeCode: z
        .string()
        .trim()
        .toUpperCase()
        .min(1, 'Enter the employee code, e.g. EBS-0001')
        .max(20)
        .regex(/^[A-Z0-9][A-Z0-9._/-]*$/, 'Use letters, digits, ".", "_", "/" or "-"'),
    hrisId: z.string().trim().max(50).optional().or(z.literal('')),
    fullName: z.string().trim().min(2, 'Enter the full name').max(150),
    displayName: z.string().trim().max(100).optional().or(z.literal('')),
    officialEmail: z
        .string()
        .trim()
        .toLowerCase()
        .email('Enter a valid email address')
        .max(150)
        .optional()
        .or(z.literal('')),
    designation: z.string().trim().max(100).optional().or(z.literal('')),
    workLocation: z.string().trim().max(80).optional().or(z.literal('')),
    countryCode,
    workMode: z.enum(WORK_MODES).default('OFFICE'),
    employmentType: z.enum(EMPLOYMENT_TYPES).default('PERMANENT'),
    status: z.enum(EMPLOYEE_STATUSES).default('ACTIVE'),
    dateOfJoining: isoDate,
    lastWorkingDay: isoDate.optional().or(z.literal('')),
    allocationType: z.enum(ALLOCATION_TYPES).default('DEDICATED'),
    billableFlag: z.enum(BILLABLE_FLAGS).default('NO'),
    /** Basis points: 50% is 5000. */
    billablePct: basisPoints.default(0),
    standardBillRate: minorAmount.optional(),
    billRateCurrency: currency.optional().or(z.literal('')),
    remarks: z.string().trim().max(2000).optional().or(z.literal('')),
};
/** The last working day cannot come before the date of joining. */
const leavesAfterJoining = (body) => !body.lastWorkingDay ||
    !body.dateOfJoining ||
    body.lastWorkingDay.slice(0, 10) >= body.dateOfJoining.slice(0, 10);
const LEAVES_BEFORE_JOINING = {
    message: 'The last working day cannot be before the date of joining',
    path: ['lastWorkingDay'],
};
export const createEmployeeRequest = z
    .object({ companyId: objectId.optional(), ...employeeFields })
    .refine(leavesAfterJoining, LEAVES_BEFORE_JOINING);
export const updateEmployeeRequest = z
    .object(employeeFields)
    .partial()
    .refine(leavesAfterJoining, LEAVES_BEFORE_JOINING);
export const employeeListQuery = paginationQuery.merge(scopeQuery).extend({
    q: z.string().trim().max(120).optional(),
    status: z.enum(EMPLOYEE_STATUSES).optional(),
});
//# sourceMappingURL=organization.js.map